import { Typography } from '@mui/material';
import { handleChange } from 'feature/form/formSlice';
import { resetMember } from 'feature/members/memberSlice';
import { resetMembers, searchMembers } from 'feature/members/membersSlice';
import { usePrevious } from 'hooks/previous.hook';
import { useCheckEligibility } from 'hooks/useCarrierCustomization';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import Box from 'styles/box/Box';
import Button from 'styles/button/Button';
import Stack from 'styles/stack/Stack';
import TextField from 'styles/textfield/TextField';
import palette from 'styles/theme/Palette';
import { IClient } from 'types/Carrier';
import { useTranslation } from 'utils/translations';

const FormPpsCode: FC<{ selectedClient: IClient }> = ({ selectedClient }) => {
	const [disabledSubmit, setDisabledSubmit] = useState<boolean>(true);
	const [value, setValue] = useState<string>('');
	const dispatch = useDispatch<AppDispatch>();
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: 'clientLabels',
	});
	const { t: tErrorLabels } = useTranslation('', {
		keyPrefix: 'errors',
	});
	const { dateOfService } = useSelector((store: RootState) => store.form);
	const { members, errorCode } = useSelector(
		(store: RootState) => store.members,
	);
	const previousFiscalCode = usePrevious(value);
	const { validationFiscalCode, checkEligibilityValidDate } =
		useCheckEligibility();

	const showCta = useMemo(() => !members || members?.length === 0, [members]);
	const isDateofServiceValid = checkEligibilityValidDate(selectedClient);

	const handleOnSubmit = (e: React.SyntheticEvent) => {
		e.preventDefault();
		if (!isDateofServiceValid) {
			dispatch(
				handleChange<any>({
					name: 'isDateOfServiceValid',
					value: isDateofServiceValid,
				}),
			);
			return;
		}
		const target = e.target as typeof e.target & {
			inputFiscalCode: { value: string };
		};
		dispatch(
			searchMembers({
				values: {
					size: 10,
					dateOfService: dateOfService,
					fiscalCode: target.inputFiscalCode.value,
				},
				carrierCode: selectedClient.carrier.code,
				externalClientId: selectedClient.externalClientId,
			}),
		);
	};

	useEffect(() => {
		if (value?.length > 0 && !!dateOfService) {
			setDisabledSubmit(false);
		} else {
			setDisabledSubmit(true);
		}
	}, [value, dateOfService]);

	useEffect(() => {
		if (members?.length && members?.length > 0 && previousFiscalCode !== value) {
			dispatch(resetMembers());
			dispatch(resetMember());
		}
	}, [dispatch, members?.length, previousFiscalCode, value]);

	return (
		<Box
			component={'form'}
			onSubmit={handleOnSubmit}
			onChange={() => !!dateOfService && !!value && setDisabledSubmit(false)}
		>
			<Stack>
				<Stack
					gap={{
						sm: '10px',
						lg: '9px',
					}}
					minHeight={{ sm: '114px', lg: '130px' }}
				>
					<TextField
						name="inputFiscalCode"
						type="text"
						label={tClientLabels('check-and-submit.searchLabels.ppsCode')}
						error={members?.length === 0}
						sx={{
							borderColor:
								members?.length === 0
									? palette.error.main
									: value.length > 0
										? palette.primary.main
										: 'none',
						}}
						onChange={e => setValue(e.target.value)}
						autoComplete={'off'}
					/>
					{members?.length === 0 || errorCode ? (
						<Typography variant="value1" color={palette.error.main}>
							{errorCode
								? tErrorLabels(String(errorCode))
								: tClientLabels('check-and-submit.errors.ppsCode')}
						</Typography>
					) : null}
				</Stack>
				{showCta && (
					<Button
						variant="contained"
						type="submit"
						sx={{
							width: { sm: 112, lg: 116 },
							justifyContent: 'center',
						}}
						disabled={disabledSubmit || !validationFiscalCode(selectedClient, value)}
					>
						{tClientLabels('commons.buttons.search')}
					</Button>
				)}
			</Stack>
		</Box>
	);
};

export default FormPpsCode;
